'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getTenancyLength, type Building } from '@repo/api';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { useAnalytics } from '@/hooks/useAnalytics';
import { TenancyLengthSection } from './TenancyLengthSection';
import { TenancyLengthSectionSkeleton } from './TenancyLengthSectionSkeleton';

interface TenancyLengthProps {
  building: Building;
  buildingAddress: string;
  showSkeleton: boolean;
  isReportReady: boolean;
}

function TenancyLength({
  building,
  buildingAddress,
  showSkeleton,
  isReportReady,
}: TenancyLengthProps) {
  const analytics = useAnalytics();
  const sectionRef = useRef<HTMLDivElement>(null);
  const onScreen = useIntersectionObserver<HTMLDivElement>(
    sectionRef,
    useMemo(() => ({ threshold: 0.5 }), []),
  );

  const [loading, setLoading] = useState(true);
  const [tenancyData, setTenancyData] = useState<any | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    if (onScreen) {
      timer = window.setTimeout(() => {
        analytics.track('rentalreport_tenancyandrenewal_scroll');
      }, 5000);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [analytics, onScreen]);

  useEffect(() => {
    if (!isReportReady) {
      setLoading(false);
      setTenancyData(null);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const response = await getTenancyLength({ buildingId: building.id });
        setTenancyData(response.tenancy);
      } catch (err) {
        console.error('[report][tenancy-length] failed to load', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [isReportReady]);

  const shouldShowSkeleton = showSkeleton || loading;
  if (shouldShowSkeleton) return <TenancyLengthSectionSkeleton />;

  return (
    <div ref={sectionRef}>
      <TenancyLengthSection
        buildingAddress={buildingAddress}
        neighborhoodName={building.neighborhood?.name}
        isBlurRequired={!isReportReady}
        tenancyData={tenancyData}
      />
    </div>
  );
}

export default TenancyLength;
